import { useCallback, useEffect, useRef, useState } from "react";
import {
  createInteractiveSlTpLineOverlay,
  InteractiveSlTpLineData,
  registerInteractiveSlTpLineOverlay,
  removeInteractiveSlTpLineOverlay,
} from "@/lib/chart-helpers/custom-overlays/interactiveSlTpLineOverlay";
import { formatOrderType } from "@/vybe-trader-client/trade-history/types";
import { useOpenedTradesStore } from "@/vybe-trader-client/opened-trades/stores/useOpenedTradesStore";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client/subscribed-symbols/stores/useSubscribedSymbolsStore";
import { calculateDefaultSlPrice } from "@/lib/chart-helpers/calculateSLTPValues";
import { getTradeOpenTime } from "@/lib/chart-helpers/trade-overlay/utils";

interface UseInteractiveSlNewProps {
  chartInstanceRef: React.MutableRefObject<any>;
  ticket: string | null;
  slEnabled: boolean;
  editableSlValue: number;
  currentSlValue?: number; // Add current reactive value from trade
  autoSlTpModify?: boolean;
  onPriceUpdate?: (price: number) => void; // Callback to update editable values
  onModify?: (type: "SL", price: number) => void;
  tradeData?: any;
  isPipsVisible?: boolean;
}

export const useInteractiveSlNew = ({
  chartInstanceRef,
  ticket,
  slEnabled,
  editableSlValue,
  currentSlValue = 0,
  autoSlTpModify = false,
  onPriceUpdate,
  onModify,
  tradeData,
  isPipsVisible = false,
}: UseInteractiveSlNewProps) => {
  const [slOverlayId, setSlOverlayId] = useState<string | null>(null);
  const slOverlayIdRef = useRef<string | null>(null);
  const ticketRef = useRef<string | null>(null);
  const prevTicketRef = useRef<string | null>(null);

  const currentTrade = useOpenedTradesStore((state) =>
    ticket ? state.allTrades.find((t) => t.ticket.toString() === ticket) : null
  );

  const tradeSymbol = currentTrade?.symbol ?? "";
  const quoteForLevels = useSubscribedSymbolsStore((state) =>
    tradeSymbol ? state.byCurrency.get(tradeSymbol)?.quote : undefined,
  );

  useEffect(() => {
    ticketRef.current = ticket;
  }, [ticket]);

  /**
   * Removes SL interactive overlays from chart using latest overlay id ref (safe on unmount; no setState).
   * Pass `sweepTicket` when `ticketRef` already advanced (e.g. switching selected trade) so pane sweep still targets the old ticket.
   */
  const tearDownSlOverlayChartArtifacts = useCallback(
    (sweepTicket?: string | null) => {
      const chart = chartInstanceRef.current;
      const tid = sweepTicket ?? ticketRef.current;
      const id = slOverlayIdRef.current;

      if (chart && id) {
        try {
          removeInteractiveSlTpLineOverlay(chart, id);
        } catch (error) {
          console.error(`Failed to remove SL overlay ${id}:`, error);
        }
      }
      slOverlayIdRef.current = null;

      if (!chart || !tid) {
        return;
      }

      try {
        const overlayMap = chart.getOverlaysByPaneId?.();
        if (!overlayMap || !(overlayMap instanceof Map)) {
          return;
        }

        overlayMap.forEach((paneOverlays) => {
          if (!(paneOverlays instanceof Map)) {
            return;
          }

          paneOverlays.forEach((overlay, overlayId) => {
            if (!overlay?.extendData) {
              return;
            }
            const data = overlay.extendData;
            const ticketMatch =
              String(data.ticket) === String(tid) && data.type === "SL";
            if (ticketMatch) {
              try {
                chart.removeOverlay(overlayId);
              } catch (error) {
                console.error("Failed to remove SL overlay:", error);
              }
            }
          });
        });
      } catch (error) {
        console.error("Error during SL cleanup:", error);
      }
    },
    [chartInstanceRef]
  );

  const cleanup = useCallback(() => {
    tearDownSlOverlayChartArtifacts();
    setSlOverlayId(null);
  }, [tearDownSlOverlayChartArtifacts]);

  useEffect(() => {
    registerInteractiveSlTpLineOverlay();
  }, []);

  useEffect(() => {
    if (!ticket) {
      return;
    }

    const unsubscribe = useOpenedTradesStore.subscribe((state, prevState) => {
      const currentTrades = state.allTrades;
      const prevTrades = prevState.allTrades;

      const tradeExistsNow = currentTrades.some(
        (t) => t.ticket.toString() === ticket
      );
      const tradeExistedBefore = prevTrades.some(
        (t) => t.ticket.toString() === ticket
      );

      if (tradeExistedBefore && !tradeExistsNow) {
        cleanup();
      }
    });

    return () => {
      unsubscribe();
    };
  }, [ticket, cleanup]);

  useEffect(() => {
    if (ticket && !currentTrade) {
      cleanup();
    }
  }, [ticket, currentTrade, cleanup]);

  useEffect(() => {
    const prev = prevTicketRef.current;
    if (prev !== null && prev !== ticket) {
      tearDownSlOverlayChartArtifacts(prev);
      setSlOverlayId(null);
    }
    prevTicketRef.current = ticket;
  }, [ticket, tearDownSlOverlayChartArtifacts]);

  const getDefaultSlPrice = () => {
    if (!currentTrade) return 0;
    return calculateDefaultSlPrice(currentTrade, {
      quote: quoteForLevels,
      digits: currentTrade.digits,
    });
  };

  const handlePriceChange = (type: "SL" | "TP", newPrice: number) => {
    if (type !== "SL") return;

    if (onPriceUpdate) {
      onPriceUpdate(newPrice);
    }

    const id = slOverlayIdRef.current;
    if (id) {
      refreshOverlay(id, newPrice);
    }
  };

  const refreshOverlay = (overlayId: string, newPrice: number) => {
    if (!chartInstanceRef.current || !currentTrade) return;

    try {
      const originalTradeValue = currentTrade.stopLoss || 0;

      const openTime = getTradeOpenTime(currentTrade);
      chartInstanceRef.current.overrideOverlay({
        id: overlayId,
        extendData: {
          price: newPrice,
          type: "SL",
          tradeType: formatOrderType(currentTrade.orderType).toUpperCase() as
            | "BUY"
            | "SELL",
          ticket: currentTrade.ticket.toString(),
          openTime,
          originalPrice: originalTradeValue,
          currentTradeValue: currentSlValue,
          autoSlTpModify: autoSlTpModify,
          isFirstAddition: false,
          tradeData: tradeData,
          isPipsVisible: isPipsVisible,
        },
        points: [
          {
            timestamp: openTime,
            value: newPrice,
          },
        ],
      });
    } catch (error) {
      console.error(`Failed to refresh SL overlay:`, error);
    }
  };

  const handleBadgeClick = (
    type: "SL" | "TP",
    action: "remove" | "modify",
    price: number
  ) => {
    if (type !== "SL") return;

    if (action === "remove") {
      if (onPriceUpdate) {
        onPriceUpdate(0);
      }
    }

    if (onModify) {
      onModify("SL", action === "remove" ? 0 : price);
    }
  };

  const handleAutoModify = (type: "SL" | "TP", price: number) => {
    if (type !== "SL") return;

    if (onModify) {
      onModify("SL", price);
    }
  };

  const getCurrentAutoSlTpModify = () => {
    return autoSlTpModify;
  };

  const hasExistingSl = (currentSlValue ?? 0) > 0;

  useEffect(() => {
    if (!chartInstanceRef.current || !currentTrade) return;

    if (slEnabled && !hasExistingSl) {
      if (!slOverlayIdRef.current) {
        let initialSlPrice = editableSlValue;

        if (editableSlValue <= 0) {
          initialSlPrice = getDefaultSlPrice();

          if (onPriceUpdate) {
            onPriceUpdate(initialSlPrice);
          }
        }

        const slLineData: InteractiveSlTpLineData = {
          price: initialSlPrice,
          type: "SL",
          tradeType: formatOrderType(currentTrade.orderType).toUpperCase() as
            | "BUY"
            | "SELL",
          ticket: currentTrade.ticket.toString(),
          openTime: getTradeOpenTime(currentTrade),
          originalPrice: currentTrade.stopLoss || 0,
          currentTradeValue: currentSlValue,
          autoSlTpModify: autoSlTpModify,
          isFirstAddition: (currentTrade.stopLoss || 0) === 0,
          tradeData: tradeData,
          isPipsVisible: isPipsVisible,
        };

        try {
          const overlayId = createInteractiveSlTpLineOverlay(
            chartInstanceRef.current,
            slLineData,
            handlePriceChange,
            handleBadgeClick,
            handleAutoModify,
            getCurrentAutoSlTpModify
          );
          if (overlayId) {
            slOverlayIdRef.current = overlayId;
            setSlOverlayId(overlayId);
          }
        } catch (error) {
          console.error("Failed to create SL overlay:", error);
        }
      } else {
        refreshOverlay(slOverlayIdRef.current, editableSlValue);
      }
    } else {
      const existingId = slOverlayIdRef.current;
      if (existingId) {
        removeInteractiveSlTpLineOverlay(chartInstanceRef.current, existingId);
        slOverlayIdRef.current = null;
        setSlOverlayId(null);
      }
    }
  }, [
    slEnabled,
    editableSlValue,
    currentSlValue,
    hasExistingSl,
    autoSlTpModify,
    isPipsVisible,
  ]);

  useEffect(() => {
    return () => {
      tearDownSlOverlayChartArtifacts();
    };
  }, [tearDownSlOverlayChartArtifacts]);

  return {
    cleanup,
  };
};
